import styled from '@emotion/styled';
import { Popover } from 'antd';
import { FC, useRef, useState } from 'react';

import { VALUE_EMPTY_TABLE } from '../../util/constants';

const InputView = styled.span`
  height: 22px;
  cursor: pointer;
  display: block;
  border-radius: 4px;
  width: 100%;
  &:hover {
    border: 1px solid #f97316;
    background-color: #ffffff;
    padding: 0 8px;
  }
`;

interface PopoverInputProps {
  value: string | null;
  onChange: (value: string) => void;
  classNameInput?: string;
  maxLength?: number;
}

/**
 * A popover-based input component that:
 * - Shows a simple text display when not active
 * - Expands to a textarea when clicked
 * - Validates changes on blur
 */
export const PopoverInput: FC<PopoverInputProps> = ({
  value,
  onChange,
  maxLength,
  classNameInput,
}) => {
  const [open, setOpen] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  /**
   * Handles popover visibility changes
   * @param newOpen - Whether the popover should open or close
   */
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  /**
   * Handles blur events on the textarea
   * - Saves changes if value was modified
   * - Closes the popover
   */
  const handleBlur = () => {
    const newValue = textAreaRef.current?.value.trim();
    if (newValue !== undefined && newValue !== value) {
      onChange(newValue);
    }
    setOpen(false);
  };

  return (
    <Popover
      content={
        <textarea
          ref={textAreaRef}
          rows={3}
          maxLength={maxLength}
          className={`border-2 focus:border-[#f97316] outline-none rounded ${classNameInput}`}
          onBlur={handleBlur}
          defaultValue={value ?? ''}
          autoFocus
        />
      }
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
    >
      <InputView
        title={value ?? VALUE_EMPTY_TABLE}
        className="truncate"
        onClick={(e) => e.stopPropagation()} // Prevent event bubbling
      >
        {value ? value : VALUE_EMPTY_TABLE}
      </InputView>
    </Popover>
  );
};
